// プレイヤー管理（オンライン一覧・OP・ホワイトリスト・BAN）。任意のコマンドではなく、決まった操作だけを受け付ける。
import { z } from 'zod';

/** Java 版の名前（3〜16 文字）。Geyser/Floodgate の Bedrock 名の接頭辞（. または *）も許す */
export const PlayerNameSchema = z.string().regex(/^[.*]?[A-Za-z0-9_]{1,16}$/, 'プレイヤー名が正しくありません');
const Reason = z
  .string()
  .max(200)
  .refine((r) => !/[\r\n\0]/.test(r), '理由に改行は使えません')
  .optional();

export const PlayerActionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('kick'), player: PlayerNameSchema, reason: Reason }).strict(),
  z.object({ action: z.literal('ban'), player: PlayerNameSchema, reason: Reason }).strict(),
  z.object({ action: z.literal('pardon'), player: PlayerNameSchema }).strict(),
  z.object({ action: z.literal('ban_ip'), ip: z.union([z.ipv4(), z.ipv6()]), reason: Reason }).strict(),
  z.object({ action: z.literal('pardon_ip'), ip: z.union([z.ipv4(), z.ipv6()]) }).strict(),
  z.object({ action: z.literal('op'), player: PlayerNameSchema }).strict(),
  z.object({ action: z.literal('deop'), player: PlayerNameSchema }).strict(),
  z.object({ action: z.literal('whitelist_add'), player: PlayerNameSchema }).strict(),
  z.object({ action: z.literal('whitelist_remove'), player: PlayerNameSchema }).strict(),
  z.object({ action: z.literal('whitelist_on') }).strict(),
  z.object({ action: z.literal('whitelist_off') }).strict(),
]);
export type PlayerAction = z.infer<typeof PlayerActionSchema>;

/** 検証済みの操作を RCON のコマンドにする */
export function playerCommand(action: PlayerAction): string {
  const withReason = (base: string, reason?: string) => (reason ? `${base} ${reason}` : base);
  switch (action.action) {
    case 'kick':
      return withReason(`kick ${action.player}`, action.reason);
    case 'ban':
      return withReason(`ban ${action.player}`, action.reason);
    case 'pardon':
      return `pardon ${action.player}`;
    case 'ban_ip':
      return withReason(`ban-ip ${action.ip}`, action.reason);
    case 'pardon_ip':
      return `pardon-ip ${action.ip}`;
    case 'op':
      return `op ${action.player}`;
    case 'deop':
      return `deop ${action.player}`;
    case 'whitelist_add':
      return `whitelist add ${action.player}`;
    case 'whitelist_remove':
      return `whitelist remove ${action.player}`;
    case 'whitelist_on':
      return 'whitelist on';
    case 'whitelist_off':
      return 'whitelist off';
  }
}

export const PlayersViewSchema = z.object({
  discord: z.record(z.string(), z.object({ id: z.string(), name: z.string() })).default({}),
  running: z.boolean(),
  online: z.array(z.string()),
  max: z.number().int().optional(),
  ops: z.array(z.object({ name: z.string(), level: z.number().int().optional() })),
  whitelist: z.array(z.object({ name: z.string() })),
  whitelistEnabled: z.boolean().optional(),
  bans: z.array(z.object({ name: z.string(), reason: z.string().optional(), created: z.string().optional() })),
  ipBans: z.array(z.object({ ip: z.string(), reason: z.string().optional(), created: z.string().optional() })),
});
export type PlayersView = z.infer<typeof PlayersViewSchema>;

/** `list` の応答（"There are 3 of a max of 20 players online: a, b, c"）を読む */
export function parseList(output: string): { online: string[]; max?: number } {
  const match = /There are (\d+) of a max(?: of)? (\d+) players online:?\s*(.*)$/s.exec(output.trim());
  if (!match) return { online: [] };
  const names = (match[3] ?? '')
    .split(',')
    .map((n) => n.trim())
    .filter(Boolean);
  return { online: names, max: Number(match[2]) };
}
